import { type JSONContent } from '@tiptap/core'
import { create, type Delta } from 'jsondiffpatch'

// Types for diff operations and statistics
export interface ContentDiffStats {
  added: number
  deleted: number
  modified: number
}

export interface DiffOperation {
  type: 'insert' | 'delete' | 'update' | 'unchanged'
  path: string[]
  value?: unknown
  oldValue?: unknown
  newValue?: unknown
}

export interface TiptapDiffResult {
  operations: DiffOperation[]
  stats: ContentDiffStats
  patch: Delta
  hasChanges: boolean
}

// Custom components that need special handling during diff
const CUSTOM_COMPONENTS = new Set([
  'card', 'cardGroup', 'step', 'steps', 'screenshot', 'admonition',
  'tabs', 'tabList', 'tab', 'tabPanel', 'accordion', 'accordionItem',
  'glossary', 'citation', 'folderContents'
])

/**
 * Creates a diff between two TipTap JSONContent documents
 * 
 * @param previous - The previous version of the document
 * @param current - The current version of the document
 * @returns TiptapDiffResult containing operations, stats, and patch
 */
export function createTiptapDiff(
  previous: JSONContent,
  current: JSONContent
): TiptapDiffResult {
  // Ensure we have valid documents
  const prev = normalizeDocument(previous)
  const curr = normalizeDocument(current)

  // Create jsondiffpatch instance with TipTap-optimized configuration
  const differ = create({
    objectHash: (obj: unknown) => {
      // Use TipTap node ID for better tracking
      if (obj && typeof obj === 'object' && obj !== null) {
        const nodeObj = obj as JSONContent
        if (nodeObj.attrs?.id) {
          return nodeObj.attrs.id
        }
        // For custom components, use type + content hash
        if (nodeObj.type && CUSTOM_COMPONENTS.has(nodeObj.type)) {
          return `${nodeObj.type}:${JSON.stringify(nodeObj.attrs || {})}`
        }
        // For text nodes, use content hash
        if (nodeObj.text) {
          return `text:${nodeObj.text}`
        }
      }
      // Default hash
      return JSON.stringify(obj)
    },
    // Array diffing for content arrays
    arrays: {
      detectMove: true,
      includeValueOnMove: false
    }
  })

  // Generate the patch
  const patch = differ.diff(prev, curr)
  const hasChanges = patch !== undefined

  // Extract operations and calculate statistics
  const operations = extractOperations(patch, [], prev, curr)
  const stats = calculateStats(operations)

  return {
    operations,
    stats,
    patch: patch || {},
    hasChanges
  }
}

/**
 * Normalizes a TipTap document for consistent diffing
 */
function normalizeDocument(doc: JSONContent): JSONContent {
  if (!doc) {
    return { type: 'doc', content: [] }
  }

  // Ensure document has required properties
  const normalized = {
    type: doc.type || 'doc',
    ...doc
  }

  // Recursively normalize content
  if (normalized.content) {
    normalized.content = normalized.content.map(normalizeNode)
  }

  return normalized
}

/**
 * Normalizes a TipTap node for consistent diffing
 */
function normalizeNode(node: JSONContent): JSONContent {
  const normalized = { ...node }

  // Ensure consistent attribute ordering for custom components
  if (CUSTOM_COMPONENTS.has(node.type || '')) {
    if (normalized.attrs) {
      // Sort attributes for consistent comparison
      const sortedAttrs: Record<string, unknown> = {}
      Object.keys(normalized.attrs).sort().forEach(key => {
        sortedAttrs[key] = normalized.attrs![key]
      })
      normalized.attrs = sortedAttrs
    }
  }

  // Recursively normalize content
  if (normalized.content) {
    normalized.content = normalized.content.map(normalizeNode)
  }

  return normalized
}

/**
 * Extracts diff operations from jsondiffpatch result
 */
function extractOperations(
  patch: Delta | undefined,
  path: string[] = [],
  original: unknown = {},
  updated: unknown = {}
): DiffOperation[] {
  if (!patch) return []

  const operations: DiffOperation[] = []

  for (const [key, value] of Object.entries(patch)) {
    const currentPath = [...path, key]

    if (Array.isArray(value)) {
      // Handle different types of diffs
      if (value.length === 1) {
        // Addition
        operations.push({
          type: 'insert',
          path: currentPath,
          value: value[0]
        })
      } else if (value.length === 2) {
        // Modification
        operations.push({
          type: 'update',
          path: currentPath,
          oldValue: value[0],
          newValue: value[1]
        })
      } else if (value.length === 3 && value[2] === 0) {
        // Deletion
        operations.push({
          type: 'delete',
          path: currentPath,
          value: value[0]
        })
      }
    } else if (typeof value === 'object' && value !== null) {
      // Nested object - recurse
      const originalValue = (original as Record<string, unknown>)?.[key]
      const updatedValue = (updated as Record<string, unknown>)?.[key]
      const nestedOps = extractOperations(
        value,
        currentPath,
        originalValue,
        updatedValue
      )
      operations.push(...nestedOps)
    }
  }

  return operations
}

/**
 * Calculates diff statistics from operations
 */
function calculateStats(operations: DiffOperation[]): ContentDiffStats {
  const stats: ContentDiffStats = {
    added: 0,
    deleted: 0,
    modified: 0
  }

  for (const op of operations) {
    switch (op.type) {
      case 'insert':
        stats.added++
        break
      case 'delete':
        stats.deleted++
        break
      case 'update':
        stats.modified++
        break
    }
  }

  return stats
}

/**
 * Applies a TipTap diff patch to a document
 * 
 * @param original - The original document
 * @param patch - The patch to apply
 * @returns The updated document
 */
export function applyTiptapPatch(
  original: JSONContent,
  patch: Delta
): JSONContent {
  if (!patch || Object.keys(patch).length === 0) {
    return original
  }

  // Create deep clone to avoid mutation
  const content = JSON.parse(JSON.stringify(original))
  
  // Apply patch using jsondiffpatch
  const patcher = create()
  const result = patcher.patch(content, patch)
  
  return result || content
}

/**
 * Checks if two TipTap documents are equivalent
 */
export function areDocumentsEqual(
  doc1: JSONContent,
  doc2: JSONContent
): boolean {
  const diff = createTiptapDiff(doc1, doc2)
  return !diff.hasChanges
}

/**
 * Gets a human-readable summary of changes
 */
export function getDiffSummary(diff: TiptapDiffResult): string {
  const { stats } = diff
  const parts: string[] = []

  if (stats.added > 0) {
    parts.push(`${stats.added} addition${stats.added === 1 ? '' : 's'}`)
  }
  
  if (stats.modified > 0) {
    parts.push(`${stats.modified} modification${stats.modified === 1 ? '' : 's'}`)
  }
  
  if (stats.deleted > 0) {
    parts.push(`${stats.deleted} deletion${stats.deleted === 1 ? '' : 's'}`)
  }

  if (parts.length === 0) {
    return 'No changes'
  }

  return parts.join(', ')
}

/**
 * Filters operations by type for processing
 */
export function getOperationsByType(
  operations: DiffOperation[],
  type: DiffOperation['type']
): DiffOperation[] {
  return operations.filter(op => op.type === type)
}

/**
 * Gets operations affecting custom components
 */
export function getCustomComponentOperations(
  operations: DiffOperation[],
  original: JSONContent,
  updated: JSONContent
): DiffOperation[] {
  return operations.filter(op => {
    // Check if operation affects a custom component
    const node = getNodeAtPath(original, op.path) || getNodeAtPath(updated, op.path)
    return node && node.type && CUSTOM_COMPONENTS.has(node.type)
  })
}

/**
 * Helper to get a node at a specific path in the document
 */
function getNodeAtPath(doc: JSONContent, path: string[]): JSONContent | null {
  let current: unknown = doc
  
  for (const segment of path) {
    if (current && typeof current === 'object') {
      current = (current as Record<string, unknown>)[segment]
    } else {
      return null
    }
  }
  
  return current as JSONContent
}

/**
 * Normalizes content to TipTap JSONContent format
 * Handles both TipTap JSONContent and legacy Plate.js Value formats
 */
function normalizeContentForDiff(content: unknown): JSONContent {
  if (!content) {
    return { type: 'doc', content: [] }
  }
  
  // If it's already a JSONContent with type field, return as is
  if (typeof content === 'object' && content !== null && 'type' in content) {
    return content as JSONContent
  }
  
  // If it's an array (legacy Plate.js format), wrap it in a doc
  if (Array.isArray(content)) {
    return {
      type: 'doc',
      content: content as JSONContent[]
    }
  }
  
  // Fallback: treat as unknown content
  return { type: 'doc', content: [] }
}

/**
 * Legacy compatibility function that mimics the original API
 * Supports both TipTap JSONContent and legacy Plate.js Value formats
 * @deprecated Use createTiptapDiff instead
 */
export function calculateContentDiffStats(
  current: unknown,
  previous: unknown
): ContentDiffStats {
  const normalizedCurrent = normalizeContentForDiff(current)
  const normalizedPrevious = normalizeContentForDiff(previous)
  
  const diff = createTiptapDiff(normalizedPrevious, normalizedCurrent)
  return diff.stats
}